import React, { MutableRefObject, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { InfiniteData } from 'react-query';
import InfiniteScroll, { Props as InfiniteScrollProps } from 'react-infinite-scroll-component';
import { EmptyList } from '@components/UIKit/EmptyList/EmptyList';
interface Props<T> {
  data: InfiniteData<{
    items: T[];
    nextCursor: number | undefined;
  }> | undefined;
  children: (props: T, index: number, pageIndex: number) => React.ReactNode;
  hasNextPage: boolean | undefined;
  fetchNextPage: () => Promise<unknown> | unknown;
  enabledRef: MutableRefObject<boolean> | undefined;
  status: 'error' | 'loading' | 'success';
  loader: InfiniteScrollProps['loader'];
  EmptyComponent?: () => JSX.Element | null;
  listClassName?: string;
  withFooter?: boolean;
  staticItemBeforeList?: JSX.Element;
  staticItemAfterList?: JSX.Element;
}
export const InfinityListWithSkeleton = <T extends Record<'id', number>,>({
  data,
  enabledRef,
  hasNextPage,
  fetchNextPage,
  listClassName,
  status,
  EmptyComponent,
  loader,
  withFooter = true,
  children,
  staticItemBeforeList,
  staticItemAfterList
}: Props<T>) => {
  const {
    ref,
    inView
  } = useInView({
    threshold: 0,
    triggerOnce: true
  });
  const dataLength = data ? data.pages.flatMap(({
    items
  }) => items).length : 0;
  useEffect(() => {
    if (!inView || typeof enabledRef === 'undefined') return;
    enabledRef.current = true;
    hasNextPage !== false && dataLength === 0 && fetchNextPage();
  }, [inView, dataLength]);
  useEffect(() => {
    if (document.body.scrollHeight <= document.body.offsetHeight + (withFooter ? 600 : 0) && hasNextPage) {
      fetchNextPage();
    }
  }, [data]);
  if (status === 'error') return null;
  if (status === 'success' && !dataLength) return EmptyComponent ? <EmptyComponent /> : <EmptyList />;
  return <div ref={ref} data-sentry-component="InfinityListWithSkeleton" data-sentry-source-file="InfinityListWithSkeleton.tsx">
            {status === 'loading' ? <div className={listClassName}>{loader}</div> : <InfiniteScroll dataLength={dataLength} next={fetchNextPage} hasMore={!!hasNextPage} loader={loader} scrollThreshold={0.65} style={{
      overflow: 'visible'
    }} className={listClassName}>
                    {typeof staticItemBeforeList !== 'undefined' && staticItemBeforeList}
                    {data?.pages?.map((group, index) => group?.items?.map((value, index1) => children(value, index1, index)))}
                    {typeof staticItemAfterList !== 'undefined' && staticItemAfterList}
                </InfiniteScroll>}
        </div>;
};